import React, { useContext, useEffect, useState } from 'react'
import { ContainerNew } from 'presentation/shared/components/ContainerNew'
import { useHistory, useLocation } from 'react-router'
import HeaderNew from 'presentation/shared/components/HeaderNew'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import ButtonNew from 'presentation/shared/components/ButtonNew'
import { ReactComponent as SearchIcon } from 'presentation/assets/icons/search.svg'
import { useServices } from 'presentation/hooks/use-services'
import CardNew from 'presentation/shared/components/CardNew'
import { GenderNewTranslated } from 'common/enum/gender'
import moment from 'moment-timezone'
import { AmbulatoryAuthorizationDashboardContext } from 'main/factories/pages/doctor/ambulatory-authorization/ambulatory-authorization-dashboard-factory'
import HeadingNew from 'presentation/shared/components/HeadingNew'
import { Patient } from 'domain/usecases/patient/get-patients'
import * as S from './styles'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import { useFormik } from 'formik'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'
import validationSchema from './validationSchema'
import { toast } from 'react-toastify'

type LocationProps = {
  isEditing?: boolean
}

const AmbulatorySolicitationLayout = WithLoading(
  ({ setIsLoading }: WithLoadingProps) => {
    const history = useHistory()
    const stateLocation = useLocation()?.state as LocationProps
    const patientService = useServices().patient
    const doctorService = useServices().doctor
    const { state, dispatch } = useContext<any>(
      AmbulatoryAuthorizationDashboardContext
    )
    const { doctor, patient } = state
    const [patientsList, setPatientsList] = useState<Patient[]>([])
    const [emptyState, setEmptyState] = useState<boolean>(false)

    const formik = useFormik({
      initialValues: {
        search_term: patient?.name || ''
      },
      validationSchema: validationSchema,
      onSubmit: async (values) => {
        try {
          setIsLoading(true)
          await getPatients(values.search_term)
        } catch (error: any) {
          toast.error(error?.message)
        } finally {
          setIsLoading(false)
        }
      }
    }) as any

    const isNumber = (str: any) => {
      return !isNaN(str) && str.trim() !== ''
    }

    const getDoctor = async () => {
      try {
        const doctor = await doctorService.loadDoctor(['doctor_id', 'name'])
        dispatch({
          type: 'DOCTOR_INFO',
          payload: doctor
        })
      } catch (error: any) {
        toast.error(
          error?.message ?? 'Ocorreu um erro ao buscar informações do médico'
        )
        history.goBack()
      }
    }

    const getPatients = async (searchTerm: string) => {
      setPatientsList([])
      setEmptyState(false)
      let payload = {}

      const cleanSearchTerm = searchTerm?.replace(/\D/g, '')

      if (cleanSearchTerm.length === 11 && isNumber(cleanSearchTerm)) {
        payload = { cpf: cleanSearchTerm }
      } else {
        payload = { name: searchTerm }
      }

      const response = await patientService.getPatients(payload)

      if (response?.length <= 0) {
        setEmptyState(true)
      }
      setPatientsList(response)
    }

    const selectPatient = (patient: Patient) => {
      const formattedPatient = {
        ...patient,
        phone: patient?.phone?.toString().replace(/\D/g, ''),
        landline: patient?.landline?.toString().replace(/\D/g, '')
      }

      dispatch({
        type: 'PATIENT_INFO',
        payload: formattedPatient
      })

      history.push('dados-do-paciente', { isEditing: stateLocation?.isEditing })
    }

    useEffect(() => {
      if (!doctor?.id && !doctor?.name) {
        getDoctor()
      }
    }, [])

    useEffect(() => {
      if (patient?.name) {
        const selectedPatient = [patient]
        setPatientsList(selectedPatient)
      }
    }, [])

    return (
      <>
        <HeaderNew
          actualPageOnClick={() =>
            stateLocation?.isEditing
              ? history.push('resumo')
              : history.push('/pedidos-autorizacao-ambulatorial/categorias')
          }
          actualPageTitle="Solicitar pedido ambulatorial"
        />
        <ContainerNew
          subHeader={
            <>
              <HeadingNew color="neutral800" size="large" weight="bold">
                Solicitar pedido ambulatorial
              </HeadingNew>
              <HeadingNew
                color="neutral800"
                size="medium"
                weight="bold"
                style={{ margin: '16px 0 4px' }}
              >
                Paciente
              </HeadingNew>
              {!stateLocation?.isEditing && (
                <SupportTextNew style={{ marginBottom: '8px' }}>
                  Busque por um paciente cadastrado ou cadastre um novo
                  paciente.
                </SupportTextNew>
              )}
              <S.ActionBox>
                <form className="input-wrapper" onSubmit={formik.handleSubmit}>
                  <TextFieldNew
                    name="search_term"
                    value={formik.values.search_term}
                    label="Paciente "
                    labelSize="xxsmall"
                    labelOverlapping
                    placeholder="Digite o nome ou CPF"
                    icon={<SearchIcon />}
                    iconPosition="insideRight"
                    iconIsButton
                    iconBgColor="primary600"
                    iconColor="white"
                    onIconClick={formik.submitForm}
                    onChange={formik.handleChange}
                    required
                    requiredColor="neutral900"
                    error={
                      formik.touched.search_term && formik.errors.search_term
                    }
                  />
                </form>

                {!stateLocation?.isEditing && (
                  <div className="button-wrapper">
                    <ButtonNew
                      type="button"
                      onClick={() =>
                        history.push('/cadastrar-novo-paciente', {
                          fromAmbulatory: true
                        })
                      }
                      size="large"
                      outlined
                      style={{ marginBottom: '24px' }}
                      fullWidth
                    >
                      Cadastrar novo paciente
                    </ButtonNew>
                  </div>
                )}
              </S.ActionBox>
            </>
          }
          style={{ paddingBottom: 0 }}
        >
          <S.Wrapper>
            {patientsList?.length > 0 ? (
              <S.CardsWrapper>
                {patientsList.map((patient, index) => (
                  <div key={index} className="card-box">
                    <CardNew
                      background="neutral50"
                      gap="8px"
                      style={{
                        width: '100%',
                        height: '100%',
                        cursor: 'pointer'
                      }}
                      onClick={() => selectPatient(patient)}
                    >
                      <div>
                        <SupportTextNew
                          color="neutral500"
                          size="xxsmall"
                          weight="semiBold"
                        >
                          Paciente
                        </SupportTextNew>
                        <SupportTextNew
                          color="neutral800"
                          size="medium"
                          weight="semiBold"
                          style={{ lineHeight: '1.1' }}
                        >
                          {patient.name}
                        </SupportTextNew>
                      </div>

                      <div>
                        <SupportTextNew
                          color="neutral500"
                          size="xxsmall"
                          weight="semiBold"
                        >
                          CPF
                        </SupportTextNew>
                        <SupportTextNew color="neutral800">
                          {patient.cpf || '-'}
                        </SupportTextNew>
                      </div>

                      <div>
                        <SupportTextNew
                          color="neutral500"
                          size="xxsmall"
                          weight="semiBold"
                        >
                          Data de nascimento
                        </SupportTextNew>
                        <SupportTextNew color="neutral800">
                          {moment.utc(patient.birth_date).format('DD/MM/YYYY')}
                        </SupportTextNew>
                      </div>

                      <div>
                        <SupportTextNew
                          color="neutral500"
                          size="xxsmall"
                          weight="semiBold"
                        >
                          Sexo biológico
                        </SupportTextNew>
                        <SupportTextNew color="neutral800">
                          {
                            GenderNewTranslated[
                              patient?.gender?.toUpperCase() as keyof typeof GenderNewTranslated
                            ]
                          }
                        </SupportTextNew>
                      </div>
                    </CardNew>
                  </div>
                ))}
              </S.CardsWrapper>
            ) : null}

            {emptyState ? (
              <div>
                <SupportTextNew size="xsmall">
                  Paciente não encontrado.
                </SupportTextNew>
                <SupportTextNew size="xsmall">
                  Cadastre novo paciente.
                </SupportTextNew>
              </div>
            ) : null}
          </S.Wrapper>
        </ContainerNew>
      </>
    )
  }
)

export default AmbulatorySolicitationLayout
